import { Component } from '@grafikr/components';
import Cookie from 'js-cookie';

import Modal from 'utils/components/modal';
import { initCountryPicker } from 'utils/languages';
import { NonNullish } from 'utils/nullish-check';

interface GeoJSResponse {
  ip: string;
  name: string;
  country: string;
  country_3: string;
}

export default Component((node, { onMounted }) => {
  const modal = new Modal(node);
  const select = NonNullish(node.querySelector<HTMLSelectElement>('[data-select]'));
  const form = NonNullish(node.querySelector('form'));

  const popupToggles = [...document.querySelectorAll<HTMLElement>('[data-toggle-geo-popup]')];

  // Check for 'country' URL param on load and remove it using history.replaceState
  const url = new URL(window.location.href);
  if (url.searchParams.has('country')) {
    url.searchParams.delete('country');
    // Also remove dangling "?" if no params left
    const cleaned = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
    window.history.replaceState({}, document.title, cleaned);
  }

  const state = {
    fallback: null as any,
    countryFromLocale: null as any,
    geoJsResponse: null as null | GeoJSResponse,
  }

  // Strip any known sublocale prefix (e.g. "/en", "/nl") from a pathname so the
  // remaining path can be re-prefixed on the destination domain.
  const stripSubLocalePrefix = (pathname: string): string => {
    const prefixes = window.theme.languages.flatMap(
      (lang) => lang.subLocales?.map((sl) => `/${sl.locale.toLowerCase()}`) ?? [],
    );

    const lower = pathname.toLowerCase();
    for (const prefix of prefixes) {
      if (lower === prefix) return '/';
      if (lower.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
    }
    return pathname;
  };

  const submitHandler = (event: SubmitEvent) => {
    event.preventDefault();

    const selectedOption = select.options[select.selectedIndex];
    const isSubLocale = selectedOption.dataset.isSubLocale !== undefined;

    const selectedLanguage = window.theme.languages.find((language) =>
      isSubLocale
        ? language.subLocales?.find((subLocale) => subLocale.locale === select.value)
        : language.locale === select.value,
    )!;

    console.log('selectedLanguage', selectedLanguage);

    let href = selectedLanguage.domain;

    if (isSubLocale) {
      const subLocale = selectedLanguage.subLocales?.find(
        (entry) => entry.locale === select.value,
      )!;
      href += `/${subLocale.locale}`;
    }

    const pathname = stripSubLocalePrefix(window.location.pathname);
    if (pathname !== '/') {
      href += pathname;
    }

    // Preserve existing query params (the `country` param was already stripped
    // from the URL on load) and add the destination country param if needed.
    const params = new URLSearchParams(window.location.search);
    if (selectedLanguage.country) {
      params.set('country', selectedLanguage.country);
    }
    const paramString = params.toString();
    if (paramString) {
      href += `?${paramString}`;
    }

    href += window.location.hash;

    window.location.href = href;
  };

  const setPreferredLanguage = () => {
    const fallback = window.theme.languages.find((language) => language.fallback);
    const localeMatch =
      window.theme.languages.find((language) => language.ISO === state.geoJsResponse?.country) ?? fallback;

    const langWithMatchingSubLocale = window.theme.languages.find((language) =>
      language.subLocales?.find((sublocale) => sublocale.country === state.geoJsResponse?.country),
    );

    const sublocaleMatch = langWithMatchingSubLocale?.subLocales!.find(
      (sublocale) => sublocale.country === state.geoJsResponse?.country,
    );
    
    console.log('localeMatch', localeMatch, {languages: window.theme.languages, res: state.geoJsResponse});

    const match = sublocaleMatch ?? localeMatch;

    if (match) {
      const optionIndex = Array.from(select.options).findIndex(
        (option) => option.value === match.locale,
      );

      select.selectedIndex = optionIndex;
    }
  };

  const buildSelectors = () => {
    initCountryPicker(select);
  };

  const initModal = () => {
    buildSelectors();

    form.addEventListener('submit', submitHandler);

    modal.open();
  };

  const init = async () => {
    const response = await fetch('https://get.geojs.io/v1/ip/country.json');

    if (response.ok) {
      const data: GeoJSResponse = await response.json();
      Cookie.set('geo', 'true');

      const fallback = window.theme.languages.find((language) => language.fallback);
      let countryFromLocale = window.theme.languages.find(
        (language) => language.locale.toLowerCase() === window.theme.request.country.toLowerCase(),
        fallback,
      )!;

      if (!countryFromLocale) {
        countryFromLocale = window.theme.languages.find(
          (language) => language.locale.toLowerCase() === window.theme.request.locale.toLowerCase(),
          fallback,
        )!;
      }

      if (!countryFromLocale) {
        countryFromLocale = fallback;
      }

      state.fallback = fallback;
      state.countryFromLocale = countryFromLocale;
      state.geoJsResponse = data;

      popupToggles.forEach((toggle) => {
        toggle.innerHTML = `${countryFromLocale.flagIcon} ${countryFromLocale.name}`;
      });

      // if (force || data.country !== countryFromLocale.country) {
      //   initModal();
      //   setPreferredLanguage(data);
      // }
    }
  };

  const startFlow = ({ force }: { force: boolean }) => {
    if (force || state.geoJsResponse?.country !== state.countryFromLocale.country) {
      initModal();
      setPreferredLanguage();
    }
  }

  const run = () => {
    if (!Cookie.get('geo')) {
      // init({ force: false });
      startFlow({ force: false });
    }
  };

  onMounted(() => {
    init();

    window.addEventListener('CookieScriptClose', () => startFlow({ force: true }), false);
    window.addEventListener('CookieScriptAccept', () => startFlow({ force: true }), false);
    window.addEventListener('CookieScriptAcceptAll', () => startFlow({ force: true }), false);
    window.addEventListener('CookieScriptReject', () => startFlow({ force: true }), false);

    if (popupToggles.length) {
      popupToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => startFlow({ force: true }));
      });
    }
  });
});
